import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { FiCopy } from 'react-icons/fi';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { MdTranslate } from 'react-icons/md';
import { RiReplyLine, RiShareForwardLine } from 'react-icons/ri';

import { ChatMessage as ChatMessageType } from '../../types';
import { getTranslationKey } from '../../modules/messagesModule/hooks/useChatTranslation';
import { PreviewDocumentDialog } from '../PreviewDocumentDialog';
import { ChatMessage, imageSrc, messageTime } from './ChatMessage';
import { ChatMessageAction, ChatMessageMenu, ChatThread, ChatThreadRef, ThreadMessageMeta } from './kit';

interface Props {
  // Newest first, as Firestore pages them.
  messages: ChatMessageType[];
  userId: string;
  // Group chats show who wrote each burst.
  showSenders?: boolean;
  loadMore: () => void;
  onDeleteMessage: (message: ChatMessageType) => void;
  onEditMessage: (message: ChatMessageType) => void;
  onReplyMessage: (message: ChatMessageType) => void;
  onForwardMessage: (message: ChatMessageType) => void;
  onReaction: (messageId: string, emoji: string) => void;
  onReactionDetails: (message: ChatMessageType) => void;
  // Enables "Translate" on incoming messages when provided
  translateMessage?: (message: ChatMessageType) => Promise<string | null>;
  emptyState?: React.ReactNode;
  className?: string;
}

export interface ChatMessageListRef {
  scrollToMessage: (messageId: string) => void;
  scrollToBottom: () => void;
}

const meta = (message: ChatMessageType): ThreadMessageMeta => ({
  // Optimistic messages have no id yet; their time is unique enough.
  id: message.id || message.localId || `local-${message.createdAt?.seconds}-${message.text}`,
  time: messageTime(message),
  author: message.senderId,
});

// Keeps the latest handler behind a stable function, so memoised bubbles
// don't re-render when a parent passes a new arrow on every render.
const useStable = <A extends any[], R>(fn: (...args: A) => R) => {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: A) => ref.current(...args), []);
};

// People-to-people messages on the shared chat thread.
export const ChatMessageList = forwardRef<ChatMessageListRef, Props>(
  (
    {
      messages,
      userId,
      showSenders = false,
      loadMore,
      onDeleteMessage,
      onEditMessage,
      onReplyMessage,
      onForwardMessage,
      onReaction,
      onReactionDetails,
      translateMessage,
      emptyState,
      className,
    },
    ref
  ) => {
    const thread = useRef<ChatThreadRef>(null);
    const chronological = useMemo(() => [...messages].reverse(), [messages]);
    const [menu, setMenu] = useState<{
      message: ChatMessageType;
      x: number;
      y: number;
      pickEmoji?: boolean;
    } | null>(null);
    const [preview, setPreview] = useState<{ images: string[]; index: number } | null>(null);
    // Translations currently shown, keyed by message id; `key` detects later edits
    const [shownTranslations, setShownTranslations] = useState<
      Record<string, { key: string; text: string }>
    >({});
    const [translatingIds, setTranslatingIds] = useState<Set<string>>(new Set());

    useImperativeHandle(ref, () => ({
      scrollToMessage: id => thread.current?.scrollToMessage(id),
      scrollToBottom: () => thread.current?.scrollToBottom(),
    }));

    const shownTranslation = (message: ChatMessageType) => {
      const shown = message.id ? shownTranslations[message.id] : undefined;
      return shown && shown.key === getTranslationKey(message) ? shown.text : undefined;
    };

    const hideTranslation = useStable((message: ChatMessageType) =>
      setShownTranslations(prev => {
        const { [message.id!]: _, ...rest } = prev;
        return rest;
      })
    );

    const toggleTranslation = async (message: ChatMessageType) => {
      const messageId = message.id;
      if (!translateMessage || !messageId || translatingIds.has(messageId)) {
        return;
      }
      if (shownTranslation(message) !== undefined) {
        hideTranslation(message);
        return;
      }
      setTranslatingIds(prev => new Set(prev).add(messageId));
      const translation = await translateMessage(message);
      setTranslatingIds(prev => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
      if (translation) {
        setShownTranslations(prev => ({
          ...prev,
          [messageId]: { key: getTranslationKey(message), text: translation },
        }));
      }
    };

    const onJumpTo = useStable((id: string) => thread.current?.scrollToMessage(id));
    const onOpenMenu = useStable((message: ChatMessageType, x: number, y: number, pickEmoji?: boolean) =>
      setMenu({ message, x, y, pickEmoji })
    );
    const react = useStable(
      (message: ChatMessageType, emoji: string) => message.id && onReaction(message.id, emoji)
    );
    const reactionDetails = useStable(onReactionDetails);
    const reply = useStable(onReplyMessage);
    const openImage = useStable((message: ChatMessageType, index: number) =>
      setPreview({ images: (message.images || []).map(imageSrc), index })
    );

    const renderMessage = (message: ChatMessageType, { first, last }: { first: boolean; last: boolean }) => {
      const isOwn = message.senderId === userId;
      return (
        <ChatMessage
          message={message}
          userId={userId}
          isOwn={isOwn}
          first={first}
          last={last}
          showSender={showSenders && !isOwn}
          translatedText={!isOwn ? shownTranslation(message) : undefined}
          translating={!!message.id && translatingIds.has(message.id)}
          onShowOriginal={hideTranslation}
          onJumpTo={onJumpTo}
          onOpenMenu={onOpenMenu}
          onReaction={react}
          onReactionDetails={reactionDetails}
          onReply={reply}
          onOpenImage={openImage}
        />
      );
    };

    const menuActions = (message: ChatMessageType): ChatMessageAction[] => {
      const isOwn = message.senderId === userId;
      const translated = shownTranslation(message);
      const text = translated ?? message.text;
      const actions: ChatMessageAction[] = [
        {
          key: 'reply',
          label: 'Reply',
          icon: <RiReplyLine size={18} />,
          onClick: () => onReplyMessage(message),
        },
        {
          key: 'forward',
          label: 'Forward',
          icon: <RiShareForwardLine size={18} />,
          onClick: () => onForwardMessage(message),
        },
      ];
      if (translateMessage && !isOwn && message.text) {
        actions.push({
          key: 'translate',
          label: translated !== undefined ? 'Show original' : 'Translate',
          icon: <MdTranslate size={18} />,
          onClick: () => toggleTranslation(message),
        });
      }
      if (text) {
        actions.push({
          key: 'copy',
          label: 'Copy text',
          icon: <FiCopy size={18} />,
          onClick: () => navigator.clipboard.writeText(text),
        });
      }
      if (isOwn && message.text) {
        actions.push({
          key: 'edit',
          label: 'Edit',
          icon: <HiOutlinePencil size={18} />,
          onClick: () => onEditMessage(message),
        });
      }
      if (isOwn) {
        actions.push({
          key: 'delete',
          label: 'Delete',
          icon: <HiOutlineTrash size={18} />,
          onClick: () => onDeleteMessage(message),
          danger: true,
        });
      }
      return actions;
    };

    return (
      <>
        <ChatThread
          ref={thread}
          className={className}
          messages={chronological}
          meta={meta}
          isOwn={message => message.senderId === userId}
          renderMessage={renderMessage}
          onLoadOlder={loadMore}
          emptyState={emptyState}
        />

        <ChatMessageMenu
          anchor={menu}
          onClose={() => setMenu(null)}
          onReact={menu ? emoji => react(menu.message, emoji) : undefined}
          actions={menu ? menuActions(menu.message) : []}
        />

        <PreviewDocumentDialog
          url={preview ? preview.images[preview.index] : ''}
          type="img"
          isOpen={!!preview}
          onClose={() => setPreview(null)}
          hasPrev={!!preview && preview.index > 0}
          hasNext={!!preview && preview.index < preview.images.length - 1}
          onPrev={() => setPreview(value => value && { ...value, index: value.index - 1 })}
          onNext={() => setPreview(value => value && { ...value, index: value.index + 1 })}
        />
      </>
    );
  }
);
